
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useState } from "react";
import { useReferralTerms } from "@/hooks/use-referral-terms";
import { DollarSign, Wallet } from "lucide-react";

interface PayoutSetupDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PayoutSetupDialog = ({ isOpen, onClose }: PayoutSetupDialogProps) => {
  const { payoutPreferences, savePayoutPreferences } = useReferralTerms();
  const [payoutMethod, setPayoutMethod] = useState<'usdc' | 'paypal'>(
    payoutPreferences?.payout_method || 'usdc'
  );
  const [usdcAddress, setUsdcAddress] = useState(payoutPreferences?.usdc_address || '');
  const [paypalEmail, setPaypalEmail] = useState(payoutPreferences?.paypal_email || '');

  const handleSave = async () => {
    try {
      await savePayoutPreferences.mutateAsync({
        payout_method: payoutMethod,
        usdc_address: payoutMethod === 'usdc' ? usdcAddress : undefined,
        paypal_email: payoutMethod === 'paypal' ? paypalEmail : undefined,
        minimum_payout: 50, // Fixed at $50
      });
      onClose();
    } catch (error) {
      console.error('Failed to save payout preferences:', error);
    }
  };

  const isValid = () => {
    if (payoutMethod === 'usdc') {
      return usdcAddress.length > 0 && usdcAddress.startsWith('0x');
    }
    if (payoutMethod === 'paypal') {
      return paypalEmail.includes('@');
    }
    return false;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Payout Setup</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium">Payout Method</Label>
            <RadioGroup 
              value={payoutMethod} 
              onValueChange={(value) => setPayoutMethod(value as 'usdc' | 'paypal')}
              className="mt-2"
            >
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="usdc" id="usdc" />
                <Label htmlFor="usdc" className="flex items-center gap-2 cursor-pointer">
                  <Wallet className="h-4 w-4" />
                  USDC (Base Blockchain)
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="paypal" id="paypal" />
                <Label htmlFor="paypal" className="flex items-center gap-2 cursor-pointer">
                  <DollarSign className="h-4 w-4" />
                  PayPal
                </Label>
              </div>
            </RadioGroup>
          </div>

          {payoutMethod === 'usdc' && (
            <div>
              <Label htmlFor="usdc-address">USDC Wallet Address (Base Network)</Label>
              <Input
                id="usdc-address"
                placeholder="0x..."
                value={usdcAddress}
                onChange={(e) => setUsdcAddress(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your Base blockchain wallet address for USDC payments
              </p>
            </div>
          )}

          {payoutMethod === 'paypal' && (
            <div>
              <Label htmlFor="paypal-email">PayPal Email Address</Label>
              <Input
                id="paypal-email"
                type="email"
                placeholder="your@email.com"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the email address associated with your PayPal account
              </p>
            </div>
          )}

          <div className="p-4 bg-gray-50 border rounded-lg">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-600">Minimum Payout Amount</Label>
              <div className="text-lg font-bold text-gray-900">$50.00</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Fixed minimum amount required before requesting a payout
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!isValid() || savePayoutPreferences.isPending}
          >
            {savePayoutPreferences.isPending ? 'Saving...' : 'Save Preferences'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
